'use client';

import {
  Box,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionProps as MuiAccordionProps,
  AccordionSummary as MuiAccordionSummary,
  SxProps,
} from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import React, { ReactNode, useState } from 'react';

// 定义面板项接口
export interface AccordionItemProps {
  key: string;
  title: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  /**
   * 自定义展开图标，可以是一个函数，接收展开状态作为参数
   */
  expandIcon?: ReactNode | ((expanded: boolean) => ReactNode);
}

// 定义Accordion组件的Props
export interface AccordionProps {
  /**
   * 折叠面板的项目列表
   */
  items: AccordionItemProps[];
  /**
   * 当前展开的面板，受控模式使用
   */
  activeKey?: string | string[];
  /**
   * 默认展开的面板，非受控模式使用
   */
  defaultActiveKey?: string | string[];
  /**
   * 展开状态变化时的回调函数
   */
  onChange?: (key: string | string[]) => void;
  /**
   * 是否允许同时展开多个面板
   * @default false
   */
  accordion?: boolean;
  /**
   * 自定义样式
   */
  sx?: SxProps;
  /**
   * 标题区域自定义样式
   */
  summaryStyle?: React.CSSProperties;
  /**
   * 内容区域自定义样式
   */
  detailsStyle?: React.CSSProperties;
  /**
   * Material UI Accordion组件的Props
   */
  muiAccordionProps?: MuiAccordionProps;
  /**
   * 全局自定义展开图标，可以是一个函数，接收展开状态作为参数
   */
  expandIcon?: ReactNode | ((expanded: boolean) => ReactNode);
  /**
   * 无边框样式
   * @default false
   */
  borderless?: boolean;

  /**
   * 自定义渲染内容
   */
  customRenderingContent?: ReactNode;
}

/**
 * Accordion折叠面板组件
 */
const Accordion = ({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  accordion = false,
  sx,
  summaryStyle,
  detailsStyle,
  muiAccordionProps,
  expandIcon,
  borderless = false,
  customRenderingContent,
}: AccordionProps) => {
  // 处理初始展开状态
  const initExpandedState = (): Record<string, boolean> => {
    const expandedState: Record<string, boolean> = {};

    if (activeKey !== undefined) {
      // 受控模式
      if (Array.isArray(activeKey)) {
        activeKey.forEach((key) => {
          expandedState[key] = true;
        });
      } else {
        expandedState[activeKey] = true;
      }
    } else if (defaultActiveKey !== undefined) {
      // 非受控模式，使用默认值
      if (Array.isArray(defaultActiveKey)) {
        defaultActiveKey.forEach((key) => {
          expandedState[key] = true;
        });
      } else {
        expandedState[defaultActiveKey] = true;
      }
    }

    return expandedState;
  };

  // 如果是受控模式，使用外部activeKey；否则使用内部状态
  const [expandedState, setExpandedState] = useState<Record<string, boolean>>(initExpandedState());

  // 处理面板展开/折叠
  const handleChange = (panelKey: string) => {
    // 受控模式，只触发回调
    if (activeKey !== undefined) {
      if (accordion) {
        // 手风琴模式，只能展开一个
        onChange?.(panelKey);
      } else {
        // 可以同时展开多个
        const currentKeys = Array.isArray(activeKey) ? [...activeKey] : [activeKey];
        const keyIndex = currentKeys.indexOf(panelKey);

        if (keyIndex > -1) {
          // 移除当前key
          currentKeys.splice(keyIndex, 1);
        } else {
          // 添加当前key
          currentKeys.push(panelKey);
        }

        onChange?.(accordion ? panelKey : currentKeys);
      }
      return;
    }

    // 非受控模式，更新内部状态
    setExpandedState((prev) => {
      const newState = { ...prev };
      if (accordion) {
        // 手风琴模式，只保留当前展开的面板
        const wasExpanded = prev[panelKey]; // 保存当前面板的原始状态
        Object.keys(newState).forEach((key) => {
          newState[key] = false;
        });
        newState[panelKey] = !wasExpanded; // 使用原始状态来决定是否展开
      } else {
        // 可以同时展开多个面板
        newState[panelKey] = !prev[panelKey];
      }

      // 触发回调
      if (onChange) {
        if (accordion) {
          onChange(newState[panelKey] ? panelKey : '');
        } else {
          const expandedKeys = Object.keys(newState).filter((key) => newState[key]);
          onChange(expandedKeys);
        }
      }

      return newState;
    });
  };

  // 获取面板是否展开
  const isPanelExpanded = (key: string): boolean => {
    if (activeKey !== undefined) {
      // 受控模式
      if (Array.isArray(activeKey)) {
        return activeKey.includes(key);
      }
      return activeKey === key;
    }
    // 非受控模式
    return !!expandedState[key];
  };

  // 获取展开图标
  const getExpandIcon = (item: AccordionItemProps, expanded: boolean): ReactNode => {
    // 优先使用item级别的自定义图标
    if (item.expandIcon) {
      return typeof item.expandIcon === 'function' ? item.expandIcon(expanded) : item.expandIcon;
    }

    // 其次使用全局自定义图标
    if (expandIcon) {
      return typeof expandIcon === 'function' ? expandIcon(expanded) : expandIcon;
    }

    // 最后使用默认图标
    return <CollapseArrowDownOutlined />;
  };

  return (
    <Box className="accordion-container">
      {items.map((item, index) => {
        const key = item.key || `${index}`;
        const expanded = isPanelExpanded(key);

        return (
          <MuiAccordion
            key={key}
            expanded={expanded}
            onChange={() => handleChange(key)}
            disabled={item.disabled}
            sx={{
              '&.MuiAccordion-root': {
                borderRadius: '5px',
                boxShadow: 'none',
                '&:before': {
                  display: 'none',
                },
                border: borderless ? 'none' : '1px solid #f0f0f0',
                mb: 2,
              },
              '&.Mui-expanded': {
                margin: 0,
                mb: 2,
              },
              ...sx,
            }}
            {...muiAccordionProps}
          >
            <MuiAccordionSummary
              expandIcon={getExpandIcon(item, expanded)}
              sx={{
                '& .MuiAccordionSummary-content': {},
                ...summaryStyle,
              }}
            >
              {typeof item.title === 'string' ? (
                <div dangerouslySetInnerHTML={{ __html: item.title }} />
              ) : (
                item.title
              )}
            </MuiAccordionSummary>
            <MuiAccordionDetails
              sx={{
                padding: '8px 16px 16px',
                ...detailsStyle,
              }}
            >
              {customRenderingContent ||
                (typeof item.content === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: item.content }} />
                ) : (
                  item.content
                ))}
            </MuiAccordionDetails>
          </MuiAccordion>
        );
      })}
    </Box>
  );
};

export default Accordion;
